import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking"); // checking | paid | pending | error
  const attempts = useRef(0);

  useEffect(() => {
    const sid = params.get("session_id");
    if (!sid) { setState("error"); return; }
    const tick = async () => {
      attempts.current += 1;
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          await refreshUser();
          setState("paid");
          return;
        }
        if (attempts.current >= 12) { setState("pending"); return; }
        setTimeout(tick, 2000);
      } catch {
        if (attempts.current >= 12) { setState("error"); return; }
        setTimeout(tick, 2000);
      }
    };
    tick();
  }, [params, refreshUser]);

  return (
    <div className="min-h-[70vh] grid place-items-center px-5">
      <div className="glass rounded-3xl p-12 text-center max-w-md w-full" data-testid="payment-success">
        {state === "checking" && (<><Loader2 className="w-12 h-12 mx-auto mb-5 animate-spin text-primary" /><h1 className="font-display text-2xl font-bold">Confirming your payment…</h1></>)}
        {state === "paid" && (<>
          <CheckCircle2 className="w-14 h-14 mx-auto mb-5 text-primary" />
          <h1 className="font-display text-3xl font-extrabold mb-2">You're a Creator! 🎬</h1>
          <p className="text-muted-foreground mb-7">Your gallery is unlocked and every video is now saved.</p>
          <div className="flex gap-3 justify-center">
            <Button onClick={() => nav("/gallery")} className="rounded-full font-semibold">Open gallery</Button>
            <Button onClick={() => nav("/studio")} variant="outline" className="rounded-full border-border font-semibold">Create</Button>
          </div>
        </>)}
        {state === "pending" && (<><Loader2 className="w-12 h-12 mx-auto mb-5 text-primary" /><h1 className="font-display text-2xl font-bold mb-2">Payment processing</h1><p className="text-muted-foreground mb-6">This is taking a moment. Your access will unlock shortly.</p><Button onClick={() => nav("/studio")} className="rounded-full font-semibold">Back to studio</Button></>)}
        {state === "error" && (<><XCircle className="w-14 h-14 mx-auto mb-5 text-destructive" /><h1 className="font-display text-2xl font-bold mb-2">Something went wrong</h1><Button onClick={() => nav("/pricing")} className="rounded-full font-semibold">Try again</Button></>)}
      </div>
    </div>
  );
}

export function PaymentCancel() {
  const nav = useNavigate();
  return (
    <div className="min-h-[70vh] grid place-items-center px-5">
      <div className="glass rounded-3xl p-12 text-center max-w-md w-full" data-testid="payment-cancel">
        <XCircle className="w-14 h-14 mx-auto mb-5 text-muted-foreground" />
        <h1 className="font-display text-2xl font-bold mb-2">Checkout cancelled</h1>
        <p className="text-muted-foreground mb-7">No charge was made. You can upgrade anytime.</p>
        <Button onClick={() => nav("/pricing")} className="rounded-full font-semibold">Back to pricing</Button>
      </div>
    </div>
  );
}
